import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { ColumnDef } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FormField } from "@/components/ui/FormField";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { DataTable } from "@/components/tables/DataTable";
import { ConfirmDialog } from "@/components/modals/ConfirmDialog";
import { GRANTABLE_PAGES } from "@/app/router/nav.config";
import { useCreateRole, useDeleteRole, useRoles, useUpdateRole } from "@/features/team/useTeam";
import { toast } from "@/store/toast.store";
import { getApiErrorMessage } from "@/api/client";
import type { CustomRole } from "@/types/auth";

const schema = z.object({
  name: z.string().min(2, "Name is required").max(60),
  pages: z.array(z.string()).min(1, "Pick at least one page"),
});
type FormValues = z.infer<typeof schema>;

export default function TeamRoles() {
  const roles = useRoles();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();

  const [editing, setEditing] = useState<CustomRole | "new" | null>(null);
  const [deleting, setDeleting] = useState<CustomRole | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });
  const selectedPages = watch("pages") ?? [];

  function openCreate() {
    reset({ name: "", pages: [] });
    setEditing("new");
  }

  function openEdit(role: CustomRole) {
    reset({ name: role.name, pages: role.pages });
    setEditing(role);
  }

  function togglePage(path: string) {
    const next = selectedPages.includes(path)
      ? selectedPages.filter((p) => p !== path)
      : [...selectedPages, path];
    setValue("pages", next, { shouldValidate: true });
  }

  function onSubmit(values: FormValues) {
    const isNew = editing === "new";
    const mutation = isNew ? createRole : updateRole;
    const payload = isNew ? values : { id: (editing as CustomRole).id, ...values };
    mutation.mutate(payload as never, {
      onSuccess: () => {
        toast.success(isNew ? "Role created" : "Role updated");
        setEditing(null);
      },
      onError: (err) => toast.error(getApiErrorMessage(err)),
    });
  }

  const columns = useMemo<ColumnDef<CustomRole, unknown>[]>(
    () => [
      { header: "Role", accessorKey: "name" },
      { header: "Pages", accessorFn: (r) => `${r.pages.length} of ${GRANTABLE_PAGES.length}` },
      {
        header: "Status",
        cell: ({ row }) => (
          <Badge tone={row.original.active ? "teal" : "neutral"}>
            {row.original.active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        header: "",
        id: "actions",
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <button
              className="rounded-md p-1.5 text-ink-faint hover:bg-cream-200 hover:text-ink"
              title="Edit"
              onClick={() => openEdit(row.original)}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              className="rounded-md p-1.5 text-ink-faint hover:bg-cream-200 hover:text-danger-500"
              title="Delete"
              onClick={() => setDeleting(row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Roles"
      description="Named roles a new admin can be assigned — each one picks which pages its sidebar shows."
      actions={
        <Button size="sm" onClick={openCreate}>
          New role
        </Button>
      }
    >
      <div className="card">
        <DataTable
          columns={columns}
          data={roles.data ?? []}
          isLoading={roles.isLoading}
          error={roles.error}
          onRetry={() => roles.refetch()}
          rowKey={(r) => r.id}
          emptyTitle="No roles yet"
          emptyDescription='Create one to start assigning admins beyond "Super Admin".'
        />
      </div>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "New role" : `Edit ${(editing as CustomRole)?.name ?? "role"}`}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              form="role-form"
              type="submit"
              variant="primary"
              loading={createRole.isPending || updateRole.isPending}
            >
              {editing === "new" ? "Create" : "Save changes"}
            </Button>
          </>
        }
      >
        <form id="role-form" className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <FormField label="Role name" error={errors.name?.message}>
            <Input placeholder="e.g. City Ops Lead" error={!!errors.name} {...register("name")} />
          </FormField>

          <div>
            <p className="mb-2 text-sm font-medium text-ink">Sidebar pages</p>
            {errors.pages && <p className="mb-2 text-xs text-danger-500">{errors.pages.message}</p>}
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3">
              {GRANTABLE_PAGES.map((page) => (
                <label key={page.path} className="flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-teal-500"
                    checked={selectedPages.includes(page.path)}
                    onChange={() => togglePage(page.path)}
                  />
                  {page.label}
                </label>
              ))}
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title={`Delete ${deleting?.name ?? "role"}?`}
        description="Every admin currently on this role must be reassigned first."
        confirmLabel="Delete"
        loading={deleteRole.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          deleteRole.mutate(deleting.id, {
            onSuccess: () => {
              toast.success("Role deleted");
              setDeleting(null);
            },
            onError: (err) => {
              toast.error(getApiErrorMessage(err));
              setDeleting(null);
            },
          });
        }}
      />
    </PageContainer>
  );
}
